<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
// Props 정의
const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    keyVal: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const insuranceListData = ref(null);
const vehicleTypeData = ref(null);
const isReadonly = computed(() => props.readonly);
const contractNo = ref(props.keyVal);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const selectedVehicleType = ref('');

const headerRentalName = computed(() => {
    if (insuranceListData.value && insuranceListData.value.length > 0) {
        return insuranceListData.value[0].rental_name || '비용';
    }
    return '비용'; // 기본값
});

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const searchQuery = ref('');
const filteredOptions = ref([]);

const filterOptions = () => {
    if (!insuranceListData.value) return;

    if (selectedVehicleType.value) {
        filteredOptions.value = insuranceListData.value.filter((option) => option.vehicle_type === selectedVehicleType.value);
    } else {
        filteredOptions.value = insuranceListData.value;
    }
};

onMounted(async () => {
    vehicleTypeData.value = commonCode('VEHICLE_TYPE').value || [];
});

const searchInsurance = async () => {
    if (contractNo.value === '') {
        insuranceListData.value = [];
        return;
    }

    const config = { method: 'post' };
    try {
        const { execute } = useAxios(`/api/contract/insurance/preview/${contractNo.value}`, config, {
            immediate: false,
            onSuccess(response) {
                const newList = response.data.data.map((item) => ({
                    value: formatCurrency(item.original_price),
                    vehicle_model: item.vehicle_model || '',
                    vehicle_type: item.vehicle_type || '',
                    vehicle_grade: item.vehicle_grade || '',
                    rental_name: item.rental_name || ''
                    //  calculate_price: item.calculate_price || ''
                }));
                insuranceListData.value = newList || [];
            },
            onError(err) {
                console.error('Error:', err.message);
                insuranceListData.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        insuranceListData.value = [];
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

watch(
    () => props.keyVal,
    (newVal) => {
        contractNo.value = newVal.toString() || '';
        searchInsurance();
        nextTick(() => {
            filteredOptions.value = insuranceListData.value;
        });
    },
    { immediate: true }
);

watch(
    insuranceListData,
    () => {
        filterOptions();
    },
    { immediate: true }
);

const emitUpdate = (value) => {
    inputValue.value = value;

    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value.replaceAll(',', '')
    });
};

// 팝업 열기/닫기
const togglePopup = () => {
    isPopupVisible.value = !isPopupVisible.value;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

// 값 선택
const selectValue = (option) => {
    emitUpdate(option.value);
    ValidationMsg.value = '';
    closePopup();
};

const clearInput = () => {
    emitUpdate('');
};

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        nextTick(() => {
            if (vehicleTypeData.value && vehicleTypeData.value.length > 0) {
                selectedVehicleType.value = vehicleTypeData.value[0].value;
            }
            filterOptions();
        });
    }
});

const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
        return '0';
    }
    return Number(value).toLocaleString();
};
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12">
            <input
                v-if="visible"
                type="text"
                :id="inputId"
                v-model="inputValue"
                class="block w-full p-3 ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'pr-24': button,
                    'error-border': ValidationMsg,
                    'readonly-Bg': isReadonly
                }"
                required
                :placeholder="placeholder"
                :required="required"
                :readonly="true"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue" @click="clearInput" class="clear-button mr-[calc(6.5rem)]">&times;</button>

                <button v-if="!isReadonly" @click="togglePopup" class="button-style">요금조회</button>
            </div>
        </div>

        <div v-if="isPopupVisible" class="popup-overlay">
            <div class="popup-content">
                <!-- <combobox :labelEmpty="true" placeholder="차종을 선택하세요." :options="vehicleTypeData"></combobox> -->
                <Select v-model="selectedVehicleType" :options="vehicleTypeData" optionLabel="text" optionValue="value" placeholder="차종을 선택하세요." class="custom-select-input" @change="filterOptions" />
                <!-- <input v-model="searchQuery" @input="filterOptions" placeholder="주소를 검색하세요" class="search-input" style="margin-top: 1rem" /> -->
                <table class="insurance-grid">
                    <thead>
                        <tr>
                            <th style="text-align: center; width: 9em; border-right: 0.1rem solid #ccc">차량구분</th>
                            <th style="text-align: center; border-right: 0.1rem solid #ccc">대표차종</th>
                            <th style="text-align: center; width: 9em">{{ headerRentalName }}</th>
                        </tr>
                    </thead>
                </table>
                <div class="options-container">
                    <table v-if="contractNo" class="insurance-grid">
                        <tbody>
                            <tr v-for="option in filteredOptions" :key="option.value" @click="selectValue(option)" class="popup-item">
                                <td style="text-align: center; width: 9em">{{ option.vehicle_grade }}</td>
                                <td style="text-align: left; padding-left: 0.8rem">{{ option.vehicle_model }}</td>
                                <td style="text-align: right; width: 9em; padding-right: 0.8rem">{{ option.value }} 원</td>
                            </tr>
                        </tbody>
                    </table>
                    <table v-else class="insurance-grid">
                        <td>표시할 데이터가 없습니다.</td>
                    </table>
                </div>
                <button @click="closePopup" class="close-button">닫기</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.error-border {
    border: 1px solid red !important;
}
.readonly-Bg {
    background-color: whitesmoke !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem; /* 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-select-input {
    width: 100%;
    height: 2.5rem;
    font-size: 0.875rem;
    font-family: 'SCoreDream400';
    background-color: white;
    border-radius: 0.5rem;
    cursor: pointer;
    padding-top: 0.1rem;
    margin-bottom: 1rem;
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    text-align: left;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    width: 40rem;
    height: 40rem;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.insurance-grid {
    width: 100%;
    border-collapse: collapse;
}

.insurance-grid th,
.insurance-grid td {
    border: 0.1rem solid #ddd;
    border-left: none;
    border-right: none;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    text-align: center;
}

.insurance-grid th {
    background-color: #f2f2f2;
}

.insurance-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.insurance-grid th:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
